﻿import type { RouteRecordRaw } from 'vue-router'
import {
  bigSaleModules,
  getModuleMenuTitleKey,
  getModuleTitleKey,
  type BigSaleModule,
  type BigSaleModuleMenu,
} from '@/data/modules'

type RouteComponent = NonNullable<RouteRecordRaw['component']>

const moduleFeatureComponents: Record<string, RouteComponent> = {
  '/sales': () => import('@/views/pages/modules/sale/SalePage.vue'),
  '/sales-orders': () => import('@/views/pages/modules/sale/SalesOrderListPage.vue'),
  '/orders': () => import('@/views/pages/modules/sale/SalesInvoiceListPage.vue'),
  '/orders/returns': () => import('@/views/pages/modules/sale/SaleReturnPage.vue'),
  '/products': () => import('@/views/pages/modules/products/ProductManagementPage.vue'),
  '/products/brands': () => import('@/views/pages/modules/products/ProductBrandPage.vue'),
  '/products/locations': () => import('@/views/pages/modules/products/ProductLocationPage.vue'),
  '/inventory/stocks': () => import('@/views/pages/modules/inventory/StockPage.vue'),
  '/inventory/checks': () => import('@/views/pages/modules/inventory/InventoryCheckPage.vue'),
  '/inventory/opening-stocks': () => import('@/views/pages/modules/inventory/OpeningStockPage.vue'),
  '/inventory/purchase-receipts': () =>
    import('@/views/pages/modules/inventory/PurchaseReceiptPage.vue'),
  '/inventory/purchase-returns': () =>
    import('@/views/pages/modules/inventory/PurchaseReturnPage.vue'),
  '/inventory/transfer-orders': () =>
    import('@/views/pages/modules/inventory/TransferOrderPage.vue'),
  '/inventory/suppliers': () => import('@/views/pages/modules/inventory/SupplierPage.vue'),
  '/inventory/production-orders': () =>
    import('@/views/pages/modules/manufacturing/ProductionOrderPage.vue'),
  '/inventory/histories': () => import('@/views/pages/modules/inventory/StockHistoryPage.vue'),
  '/orders/cash-book': () => import('@/views/pages/modules/sale/CashBookPage.vue'),
  '/orders/cash-transactions': () => import('@/views/pages/modules/sale/CashTransactionPage.vue'),
  '/orders/cash-book-types': () => import('@/views/pages/modules/sale/CashBookTypePage.vue'),
  '/settings/branches': () => import('@/views/pages/modules/settings/BranchManagementPage.vue'),
  '/settings/billiard-lights': () =>
    import('@/views/pages/modules/settings/BilliardLightDevicePage.vue'),
  '/settings/system': () => import('@/views/pages/modules/settings/SystemSettingsPage.vue'),
  '/settings/print-templates': () =>
    import('@/views/pages/modules/settings/PrintTemplatesPage.vue'),
  '/settings/payment-methods': () => import('@/views/pages/modules/settings/PaymentMethodPage.vue'),
  '/settings/delivery-partners': () =>
    import('@/views/pages/modules/inventory/DeliveryPartnerPage.vue'),
  '/settings/units': () => import('@/views/pages/modules/products/ProductUnitPage.vue'),
  '/settings/product-groups': () => import('@/views/pages/modules/products/GroupProductPage.vue'),
  '/settings/activity-logs': () => import('@/views/pages/modules/settings/SystemHistoryPage.vue'),
  '/settings/about': () => import('@/views/pages/modules/settings/DeveloperInfoPage.vue'),
  '/staffs': () => import('@/views/pages/modules/staff/StaffManagementPage.vue'),
  '/staffs/contracts': () => import('@/views/pages/modules/staff/StaffContractPage.vue'),
  '/staffs/contracts/types': () => import('@/views/pages/modules/staff/StaffContractTypePage.vue'),
  '/staffs/offboardings': () => import('@/views/pages/modules/staff/StaffOffboardingPage.vue'),
  '/staffs/leave': () => import('@/views/pages/modules/staff/StaffPayrollInputPage.vue'),
  '/staffs/overtime-requests': () =>
    import('@/views/pages/modules/staff/StaffPayrollInputPage.vue'),
  '/staffs/allowances': () => import('@/views/pages/modules/staff/StaffPayrollInputPage.vue'),
  '/staffs/salary-advances': () => import('@/views/pages/modules/staff/StaffPayrollInputPage.vue'),
  '/staffs/insurance': () => import('@/views/pages/modules/staff/StaffPayrollInputPage.vue'),
  '/staffs/payroll': () => import('@/views/pages/modules/staff/StaffPayrollPage.vue'),
  '/staffs/payroll/payslips': () => import('@/views/pages/modules/staff/StaffPayrollPage.vue'),
  '/staffs/roles': () => import('@/views/pages/modules/staff/RoleManagementPage.vue'),
  '/staffs/departments': () => import('@/views/pages/modules/staff/StaffDepartmentPage.vue'),
  '/staffs/shifts': () => import('@/views/pages/modules/staff/StaffShiftPage.vue'),
  '/staffs/attendances': () => import('@/views/pages/modules/staff/StaffAttendancePage.vue'),
  '/staffs/attendances/online': () =>
    import('@/views/pages/modules/staff/StaffOnlineAttendancePage.vue'),
  '/staffs/attendances/history': () =>
    import('@/views/pages/modules/staff/StaffAttendanceHistoryPage.vue'),
  '/staffs/attendances/monthly-overview': () =>
    import('@/views/pages/modules/staff/StaffAttendanceMonthlyOverviewPage.vue'),
  '/staffs/attendances/total-time': () =>
    import('@/views/pages/modules/staff/StaffAttendanceTotalTimePage.vue'),
  '/prices/products': () => import('@/views/pages/modules/prices/PriceListManagementPage.vue'),
  '/prices/areas': () => import('@/views/pages/modules/prices/AreaPricingManagementPage.vue'),
  '/promotions/coupons': () => import('@/views/pages/modules/promotions/VoucherManagementPage.vue'),
  '/prices/table-areas': () =>
    import('@/views/pages/modules/prices/TablePricingManagementPage.vue'),
  '/tables': () => import('@/views/pages/modules/tables/SalesTableManagementPage.vue'),
  '/tables/areas': () => import('@/views/pages/modules/tables/SalesAreaManagementPage.vue'),
  '/customers': () => import('@/views/pages/modules/customer/CustomerPage.vue'),
  '/customers/groups': () => import('@/views/pages/modules/customer/CustomerGroupPage.vue'),
  '/customers/tiers': () => import('@/views/pages/modules/customer/MembershipTierPage.vue'),
  '/customers/loyalty-points': () => import('@/views/pages/modules/customer/LoyaltyPointPage.vue'),
  '/products/sale-online': () => import('@/views/pages/modules/channel/SaleOnlinePage.vue'),
  '/reports/online-stats': () => import('@/views/pages/modules/channel/OnlineStatsPage.vue'),
  '/settings/online-web': () => import('@/views/pages/modules/channel/OnlineConfigPage.vue'),
  '/customers/debts': () => import('@/views/pages/modules/customer/CustomerDebtPage.vue'),

  '/dashboard': () => import('@/views/pages/modules/dashboard/DashboardPage.vue'),
  '/dashboard/reports-revenue': () =>
    import('@/views/pages/modules/dashboard/DashboardRevenueReportPage.vue'),
  '/dashboard/reports-finance': () =>
    import('@/views/pages/modules/dashboard/DashboardFinanceReportPage.vue'),
  '/reports/shift-revenue': () =>
    import('@/views/pages/modules/dashboard/ShiftRevenueReportPage.vue'),
  '/reports/shift-closing': () =>
    import('@/views/pages/modules/dashboard/ShiftClosingReportPage.vue'),
  '/reports/day-closing': () => import('@/views/pages/modules/dashboard/DayClosingReportPage.vue'),
}

function trimLeadingSlash(path: string) {
  return path.replace(/^\/+/, '')
}

function getMenuChildPath(module: BigSaleModule, menu: BigSaleModuleMenu) {
  if (menu.path === module.path) {
    return ''
  }

  const modulePrefix = `${module.path}/`

  if (menu.path.startsWith(modulePrefix)) {
    return trimLeadingSlash(menu.path.slice(modulePrefix.length))
  }

  return menu.path
}

function getMenuRouteName(module: BigSaleModule, menu: BigSaleModuleMenu) {
  if (menu.path.startsWith('/inventory/')) {
    return `module-inventory-${trimLeadingSlash(menu.path.replace('/inventory/', '')).replace(
      /\//g,
      '-',
    )}`
  }

  if (menu.path === '/prices/products' || menu.path.startsWith('/promotions')) {
    return `module-price-${trimLeadingSlash(menu.path)
      .replace(/\//g, '-')
      .replace(/^prices-/, '')}`
  }

  const childPath = getMenuChildPath(module, menu)

  if (!childPath) {
    return `module-${module.id}-index`
  }

  return `module-${module.id}-${trimLeadingSlash(childPath).replace(/\//g, '-')}`
}

function getMenuComponent(menu: BigSaleModuleMenu) {
  return (
    moduleFeatureComponents[menu.path] ??
    (() => import('@/views/pages/modules/ModuleFeaturePage.vue'))
  )
}

function createModuleRoute(module: BigSaleModule): RouteRecordRaw {
  const moduleMenus = module.menus.filter((menu) => menu.routeMode !== 'standalone')
  const firstMenu = moduleMenus[0]
  const redirect = firstMenu && firstMenu.path !== module.path ? firstMenu.path : undefined

  return {
    path: trimLeadingSlash(module.path),
    name: `module-${module.id}`,
    component: () => import('@/views/layouts/ModuleLayout.vue'),
    redirect,
    meta: {
      requiresAuth: true,
      moduleId: module.id,
      moduleCode: module.code,
      moduleTitle: module.title,
      moduleTitleKey: getModuleTitleKey(module),
      moduleIcon: module.icon,
      modulePath: module.path,
      permissions: module.permissions,
    },
    children: [
      ...moduleMenus.map((menu) => ({
        path: getMenuChildPath(module, menu),
        name: getMenuRouteName(module, menu),
        component: getMenuComponent(menu),
        meta: {
          requiresAuth: true,
          moduleId: module.id,
          moduleCode: module.code,
          moduleTitle: module.title,
          moduleTitleKey: getModuleTitleKey(module),
          moduleIcon: module.icon,
          modulePath: module.path,
          menuTitle: menu.title,
          menuTitleKey: getModuleMenuTitleKey(module, menu),
          menuIcon: menu.icon,
          menuPath: menu.path,
          permissions: menu.permissions,
        },
      })),
      ...(module.id === 'staff'
        ? [
            {
              path: 'leave/types',
              name: 'module-staff-leave-types',
              component: () => import('@/views/pages/modules/staff/StaffLeavePolicyPage.vue'),
              meta: {
                requiresAuth: true,
                moduleId: module.id,
                moduleCode: module.code,
                moduleTitle: module.title,
                moduleTitleKey: getModuleTitleKey(module),
                moduleIcon: module.icon,
                modulePath: module.path,
                menuTitleKey: 'payroll.leave_type_list',
                menuPath: '/staffs/leave',
                permissions: ['staff_leave.manage'],
              },
            },
            {
              path: 'leave/quota',
              name: 'module-staff-leave-quota',
              component: () => import('@/views/pages/modules/staff/StaffLeavePolicyPage.vue'),
              meta: {
                requiresAuth: true,
                moduleId: module.id,
                moduleCode: module.code,
                moduleTitle: module.title,
                moduleTitleKey: getModuleTitleKey(module),
                moduleIcon: module.icon,
                modulePath: module.path,
                menuTitleKey: 'payroll.quota_grant_list',
                menuPath: '/staffs/leave',
                permissions: ['staff_leave.manage'],
              },
            },
            {
              path: 'payroll/tables/:periodId',
              name: 'module-staff-payroll-table-detail',
              component: () =>
                import('@/views/pages/modules/staff/StaffPayrollTableDetailPage.vue'),
              meta: {
                requiresAuth: true,
                moduleId: module.id,
                moduleCode: module.code,
                moduleTitle: module.title,
                moduleTitleKey: getModuleTitleKey(module),
                moduleIcon: module.icon,
                modulePath: module.path,
                menuTitleKey: 'payroll.show_payroll_sheet',
                menuPath: '/staffs/payroll',
                permissions: ['staff_payroll.view'],
              },
            },
            {
              path: ':id(\\d+)',
              name: 'module-staff-profile-detail',
              component: () => import('@/views/pages/modules/staff/StaffProfileDetailPage.vue'),
              meta: {
                requiresAuth: true,
                moduleId: module.id,
                moduleCode: module.code,
                moduleTitle: module.title,
                moduleTitleKey: getModuleTitleKey(module),
                moduleIcon: module.icon,
                modulePath: module.path,
                menuTitleKey: 'staff.profile.title',
                menuPath: '/staffs',
                permissions: ['staff.view'],
              },
            },
          ]
        : []),
    ],
  }
}

const moduleRoutes = bigSaleModules.filter((module) => module.id !== 'pos').map(createModuleRoute)

export const dashboardRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/layouts/AppLayout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'DefaultHomePage',
        component: () => import('@/views/pages/Dashboard.vue'),
      },
      {
        path: 'account/profile',
        name: 'account-profile',
        component: () => import('@/views/pages/account/AccountProfilePage.vue'),
        meta: {
          requiresAuth: true,
          pageTitleKey: 'auth.profile.title',
        },
      },
      ...moduleRoutes,
      {
        path: 'inventory/delivery-partners',
        redirect: '/settings/delivery-partners',
        meta: {
          requiresAuth: true,
          permissions: ['delivery_partner.view'],
          moduleId: 'setting',
        },
      },
      {
        path: 'products/units',
        redirect: '/settings/units',
        meta: {
          requiresAuth: true,
          permissions: ['product.unit.view'],
          moduleId: 'setting',
        },
      },
      {
        path: 'products/groups',
        redirect: '/settings/product-groups',
        meta: {
          requiresAuth: true,
          permissions: ['product.category.view'],
          moduleId: 'setting',
        },
      },
      {
        path: 'inventory',
        redirect: '/inventory/stocks',
        meta: {
          requiresAuth: true,
          permissions: ['inventory.view'],
          moduleId: 'product',
        },
      },
      {
        path: 'prices',
        redirect: '/prices/products',
        meta: {
          requiresAuth: true,
          permissions: ['price_list.view'],
          moduleId: 'product',
        },
      },
      {
        path: 'customers/:id',
        name: 'module-customer-detail',
        component: () => import('@/views/pages/modules/customer/CustomerDetailPage.vue'),
        meta: { requiresAuth: true, permissions: ['customer.view'], moduleId: 'customer' },
      },
    ],
  },
]
